import { CacheManager } from '../render/utils/cache-manager';

export class CacheProvider {
  constructor(options = {}) {
    this.stageContext = null;
    this.cacheManager = new CacheManager({
      maxSize: options.maxSize || 300,
      maxAge: options.maxAge || 60 * 60 * 1000, // 30 seconds
    });

    // 定义不同类型的缓存
    this.cacheTypes = {
      UNKNOWN: 'unknown',
      LOAD_IMAGE: 'loadImage',
      RENDER_IMAGE: 'renderImage',
      RENDER_TEXT: 'renderText',
      IMAGE_FILTER: 'imageFilter',
      IMAGE_CLIP: 'imageClip',
      RENDER: 'render',
      BACKGROUND: 'background',
      TEXT_MEASURE: 'textMeasure',
      FONT: 'font',
      FONT_CHECK: 'fontCheck',
      SHAPE: 'shape',
      SHADER_FILL: 'shaderFill',
    };
  }

  attach(stageContext) {
    this.stageContext = stageContext;

    // 将缓存方法绑定到渲染上下文
    if (stageContext.cache) {
      // 替换原有的简单对象缓存为智能缓存管理器
      const cache = {
        get: (type, key) => this.get(type, key),
        set: (type, key, value) => this.set(type, key, value),
        clear: (type) => this.clear(type),
        clearAll: () => this.clearAll(),
        getStats: () => this.getStats(),
        create: (type) => {
          if (!type) type = this.cacheTypes.UNKNOWN;
          const local = cache[type];
          if (local) return local;
          cache[type] = {
            get: (key) => this.get(type, key),
            has: (key) => this.has(type, key),
            set: (key, value) => this.set(type, key, value),
          };
          return cache[type];
        },
        // 保持向后兼容性
        loadImage: {
          get: (key) => this.get(this.cacheTypes.LOAD_IMAGE, key),
          set: (key, value) => this.set(this.cacheTypes.LOAD_IMAGE, key, value),
        },
        renderText: {
          remove: (key) => this.remove(this.cacheTypes.RENDER_TEXT, key),
          get: (key) => this.get(this.cacheTypes.RENDER_TEXT, key),
          set: (key, value) => this.set(this.cacheTypes.RENDER_TEXT, key, value),
        },
        shaderFill: {
          remove: (key) => this.remove(this.cacheTypes.SHADER_FILL, key),
          get: (key) => this.get(this.cacheTypes.SHADER_FILL, key),
          set: (key, value) => this.set(this.cacheTypes.SHADER_FILL, key, value),
        },
        imageFilter: {
          get: (key) => this.get(this.cacheTypes.IMAGE_FILTER, key),
          set: (key, value) => this.set(this.cacheTypes.IMAGE_FILTER, key, value),
        },
        imageClip: {
          get: (key) => this.get(this.cacheTypes.IMAGE_CLIP, key),
          set: (key, value) => this.set(this.cacheTypes.IMAGE_CLIP, key, value),
        },
        renderImage: {
          remove: (key) => this.remove(this.cacheTypes.RENDER_IMAGE, key),
          get: (key) => this.get(this.cacheTypes.RENDER_IMAGE, key),
          set: (key, value) => this.set(this.cacheTypes.RENDER_IMAGE, key, value),
        },
        render: {
          get: (key) => this.get(this.cacheTypes.RENDER, key),
          set: (key, value) => this.set(this.cacheTypes.RENDER, key, value),
        },
        background: {
          get: (key) => this.get(this.cacheTypes.BACKGROUND, key),
          set: (key, value) => this.set(this.cacheTypes.BACKGROUND, key, value),
        },
        fontCheck: {
          clear: () => this.clear(this.cacheTypes.FONT_CHECK),
          get: (key) => this.get(this.cacheTypes.FONT_CHECK, key),
          set: (key, value) => this.set(this.cacheTypes.FONT_CHECK, key, value),
        },
        font: {
          clear: () => this.clear(this.cacheTypes.FONT),
          get: (key) => this.get(this.cacheTypes.FONT, key),
          set: (key, value) => this.set(this.cacheTypes.FONT, key, value),
        },
      };
      stageContext.cache = cache;
    }
  }

  dispose() {
    if (this.cacheManager) {
      this.cacheManager.dispose();
      this.cacheManager = null;
    }
    this.stageContext = null;
  }

  // 获取缓存
  get(type, key) {
    if (!this.cacheManager) return null;
    return this.cacheManager.get(type, key);
  }

  has(type, key) {
    if (!this.cacheManager) return null;
    return this.cacheManager.has(type, key);
  }

  // 设置缓存
  set(type, key, value) {
    if (!this.cacheManager) return;
    this.cacheManager.set(type, key, value);
  }

  remove(type, key) {
    if (!this.cacheManager) return;
    this.cacheManager.remove(type, key);
  }

  // 清理特定类型的缓存
  clear(type) {
    if (!this.cacheManager) return;
    this.cacheManager.clear(type);
  }

  // 清理所有缓存
  clearAll() {
    if (!this.cacheManager) return;
    this.cacheManager.clearAll();
  }

  // 获取缓存统计信息
  getStats() {
    if (!this.cacheManager) return {};
    return this.cacheManager.getStats();
  }

  // 预热缓存 - 可选的优化方法
  preloadCache(type, items) {
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      if (item.key && item.value) {
        this.set(type, item.key, item.value);
      }
    });
  }

  // 获取缓存使用情况
  getCacheUsage() {
    const stats = this.getStats();
    const usage = {};

    Object.keys(this.cacheTypes).forEach((typeKey) => {
      const type = this.cacheTypes[typeKey];
      usage[type] = stats[type] || { size: 0, maxSize: 0 };
    });

    return usage;
  }

  // 内存压力下的紧急清理
  emergencyCleanup() {
    // 优先清理渲染缓存，保留图片加载缓存
    this.clear(this.cacheTypes.RENDER);
    this.clear(this.cacheTypes.RENDER_IMAGE);
    this.clear(this.cacheTypes.TEXT_MEASURE);

    // 如果仍需要更多空间，再清理其他缓存
    setTimeout(() => {
      this.clear(this.cacheTypes.BACKGROUND);
      this.clear(this.cacheTypes.SHAPE);
    }, 100);
  }
}
